import { useEffect, useMemo } from 'react';
import { CalculationFormula, DRVGuideline } from '@/types/calculation-formulas';
import { ViableFormulasResponse } from '@/lib/api/clients';
import { calculateAgeInMonthsFromDateString } from '../date-utils';
import {
  findHenryOxfordFormula,
  getPreferredFormulaFallback,
  shouldPreferHenryOxfordForAge,
} from '../formula-utils';

interface UseDefaultValuesParams {
  currentStep: number;
  formData: {
    formulaId?: string;
    activityLevel?: string;
    drvFormulaId?: string;
    dateOfBirth?: string;
  };
  allFormulas: CalculationFormula[];
  viableFormulas: CalculationFormula[];
  defaultFormulaId: string | null;
  drvGuidelines: DRVGuideline[];
  defaultDRVGuidelineId: string | null;
  allowedActivityLevels: ViableFormulasResponse['allowedActivityLevels'];
  handleInputChange: (field: string, value: string) => void;
}

export const useDefaultValues = ({
  currentStep,
  formData,
  allFormulas,
  viableFormulas,
  defaultFormulaId,
  drvGuidelines,
  defaultDRVGuidelineId,
  allowedActivityLevels,
  handleInputChange,
}: UseDefaultValuesParams) => {
  // Calculate age in months to determine if client is an infant (0-12 months)
  const ageInMonths = useMemo(() => {
    return calculateAgeInMonthsFromDateString(formData.dateOfBirth);
  }, [formData.dateOfBirth]);
  
  const isInfant = ageInMonths !== null && ageInMonths >= 0 && ageInMonths < 12;

  // Set default formula when moving to step 2 and formulas are loaded
  useEffect(() => {
    if (currentStep === 2 && allFormulas.length > 0 && !formData.formulaId) {
      let formulaToSet = null;
      const preferHenryOxford = shouldPreferHenryOxfordForAge(ageInMonths);

      if (preferHenryOxford) {
        formulaToSet = findHenryOxfordFormula(viableFormulas) ?? findHenryOxfordFormula(allFormulas) ?? null;
      }

      if (!formulaToSet && defaultFormulaId) {
        formulaToSet = viableFormulas.find(f => f.id === defaultFormulaId) ?? null;
      }

      if (!formulaToSet && viableFormulas.length > 0) {
        formulaToSet = getPreferredFormulaFallback(viableFormulas, allFormulas) ?? null;
      }

      if (!formulaToSet && allFormulas.length > 0) {
        formulaToSet = defaultFormulaId
          ? allFormulas.find(f => f.id === defaultFormulaId) ?? allFormulas[0]
          : allFormulas[0];
      }

      if (formulaToSet) {
        handleInputChange('formulaId', String(formulaToSet.id));
      }
    }
  }, [currentStep, allFormulas, viableFormulas, formData.formulaId, defaultFormulaId, ageInMonths, handleInputChange]);

  // Set default activity level when allowedActivityLevels is loaded
  // Keep activity level in sync with age-specific allowed options.
  useEffect(() => {
    if (currentStep !== 2) return;

    const options = allowedActivityLevels?.options ?? [];

    if (isInfant) {
      if (formData.activityLevel) {
        handleInputChange('activityLevel', '');
      }
      return;
    }

    if (options.length === 0) return;

    const hasCurrentSelection = Boolean(
      formData.activityLevel &&
      options.some((option) => option.value === formData.activityLevel)
    );
    if (hasCurrentSelection) return;

    const defaultOption = options.find((option) => option.isDefault) || options[0];
    if (defaultOption && defaultOption.value) {
      handleInputChange('activityLevel', defaultOption.value);
    }
  }, [currentStep, allowedActivityLevels, formData.activityLevel, isInfant, handleInputChange]);

  // Set default DRV guideline when moving to step 2 and DRV guidelines are loaded
  useEffect(() => {
    if (currentStep === 2 && drvGuidelines.length > 0 && !formData.drvFormulaId) {
      let drvGuidelineToSet = null;

      if (defaultDRVGuidelineId) {
        drvGuidelineToSet = drvGuidelines.find(g => g.id === defaultDRVGuidelineId);
      }

      if (!drvGuidelineToSet && drvGuidelines.length > 0) {
        drvGuidelineToSet = drvGuidelines[0];
      }

      if (drvGuidelineToSet) {
        handleInputChange('drvFormulaId', String(drvGuidelineToSet.id));
      }
    }
  }, [currentStep, drvGuidelines, formData.drvFormulaId, defaultDRVGuidelineId, handleInputChange]);
};
